/**
 * Client half of the staff "Preview as a parent" lock (#5634, KTD2 layer 2).
 *
 * While armed, every same-origin `/api` call carries the preview header, and
 * any call off the read allowlist is answered here with the standard 403
 * refusal without reaching the network, whatever its method. Calls to other
 * origins pass untouched: the components that talk to Stripe never mount in a
 * preview (PREVIEW_NEVER_MOUNT_MODULES).
 *
 * Browser-only. Sign-out disarms it first (AuthContext.signOut), so leaving the
 * account never trips the lock.
 */

import { createLogger } from '@/utils/logger/devLogger';
import {
  ENROLLMENT_PREVIEW_HEADER,
  PREVIEW_WRITE_REFUSED_BODY,
  isPreviewReadAllowed,
} from './previewLock';

const logger = createLogger('EnrollmentPreviewFetchGuard');

let originalFetch: typeof fetch | null = null;
let guardedFetch: typeof fetch | null = null;
// Read on every call, so a copy of the wrapper that something else captured passes calls
// straight through once the preview ends (disarm cannot unwrap a wrapper added on top of it).
let armed = false;

function requestParts(input: RequestInfo | URL, init?: RequestInit): { url: URL; method: string } | null {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(raw, window.location.origin);
    const method = (init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET')).toUpperCase();
    return { url, method };
  } catch {
    return null;
  }
}

function withPreviewHeader(input: RequestInfo | URL, init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers ?? (typeof input === 'object' && 'headers' in input ? input.headers : undefined));
  headers.set(ENROLLMENT_PREVIEW_HEADER, '1');
  return { ...init, headers };
}

export function isPreviewFetchGuardArmed(): boolean {
  return armed;
}

/** Arms the guard (idempotent). Returns the matching disarm. */
export function armPreviewFetchGuard(): () => void {
  if (typeof window === 'undefined') return () => {};
  armed = true;
  if (guardedFetch === null || window.fetch !== guardedFetch) {
    const unguarded = window.fetch;
    originalFetch = unguarded;
    const passThrough = (input: RequestInfo | URL, init?: RequestInit) => unguarded.call(window, input, init);
    guardedFetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
      if (!armed) return passThrough(input, init);
      const parts = requestParts(input, init);
      if (!parts || parts.url.origin !== window.location.origin || !parts.url.pathname.startsWith('/api/')) {
        return passThrough(input, init);
      }
      if (!isPreviewReadAllowed(parts.method, parts.url.pathname)) {
        logger.warn('Preview blocked a call that is not a listed read', {
          method: parts.method,
          path: parts.url.pathname,
        });
        return new Response(JSON.stringify(PREVIEW_WRITE_REFUSED_BODY), {
          status: 403,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      return passThrough(input, withPreviewHeader(input, init));
    }) as typeof fetch;
    window.fetch = guardedFetch;
  }
  return disarmPreviewFetchGuard;
}

export function disarmPreviewFetchGuard(): void {
  if (typeof window === 'undefined' || !armed) return;
  armed = false;
  // Unwrap only if nothing wrapped fetch after us; otherwise the stranded wrapper passes through.
  if (guardedFetch !== null && window.fetch === guardedFetch && originalFetch !== null) {
    window.fetch = originalFetch;
    guardedFetch = null;
    originalFetch = null;
  }
}
